import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";

const LINES = [
    { parts: [{ text: "We Design.", gradient: false }] },
    { parts: [{ text: "We Develop.", gradient: true }] },
    { parts: [{ text: "We Build Digital", gradient: false }, { text: " Experiences.", gradient: true }] },
];

const lineVariants = {
    hidden: { y: "110%", rotate: 2 },
    visible: (i) => ({
        y: "0%",
        rotate: 0,
        transition: { delay: 0.15 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    }),
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const visualY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-[76px]">
            <div className="absolute inset-0 grain-overlay pointer-events-none" />
            <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-violet-400/25 blur-[130px] pointer-events-none" />
            <div className="absolute top-1/3 -right-40 w-[480px] h-[480px] rounded-full bg-pink-400/20 blur-[130px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-amber-300/20 blur-[130px] pointer-events-none" />

            <motion.div
                style={{ y: visualY }}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:block absolute inset-y-0 right-0 w-[42%] pointer-events-none"
                data-testid="hero-illustration-desktop"
            >
                <img
                    src={IMAGES.hero}
                    alt="Hand-drawn Brain Brush team sketch — the creative people behind the studio"
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-[92%] w-auto max-w-full object-contain mix-blend-multiply"
                    loading="eager"
                />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 lg:py-24 w-full">
                <motion.div style={{ y: textY }} className="lg:max-w-[58%]">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white/70 backdrop-blur px-4 py-1.5 mb-8"
                        data-testid="hero-badge"
                    >
                        <Sparkles size={14} className="text-violet-600" />
                        <span className="font-mono-brand text-xs tracking-[0.22em] uppercase font-semibold text-violet-700">
                            Creative Technology Agency — Est. 2019
                        </span>
                    </motion.div>

                    <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-slate-900 text-4xl sm:text-5xl lg:text-6xl">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block will-change-transform"
                                    custom={i}
                                    variants={lineVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {line.parts.map((part, j) =>
                                        part.gradient ? (
                                            <span key={j} className="gradient-text">{part.text}</span>
                                        ) : (
                                            <span key={j}>{part.text}</span>
                                        )
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.8 }}
                        className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
                        data-testid="hero-subheadline"
                    >
                        Brain Brush is a digital design and development agency. For 7+ years we've transformed ideas
                        into beautiful, intelligent and impactful products for regional and international clients.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-9 flex flex-wrap gap-4"
                    >
                        <button
                            data-testid="hero-cta-explore-services"
                            onClick={() => scrollToId("services")}
                            className="gradient-bg text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2 shadow-xl shadow-violet-500/25 hover:shadow-pink-500/35 hover:scale-[1.03] transition-all duration-300"
                        >
                            Explore Our Services <ArrowRight size={16} />
                        </button>
                        <button
                            data-testid="hero-cta-get-in-touch"
                            onClick={() => scrollToId("contact")}
                            className="bg-white/70 backdrop-blur border border-slate-200 text-slate-900 font-semibold px-7 py-3.5 rounded-full hover:border-violet-400 hover:text-violet-700 transition-all duration-300"
                        >
                            Get in Touch
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 0.8 }}
                        className="mt-12 grid grid-cols-3 gap-2.5 sm:gap-3"
                    >
                        {[
                            { value: "7+", label: "Years of experience" },
                            { value: "20+", label: "Global clients" },
                            { value: "4", label: "Core disciplines" },
                        ].map((stat) => (
                            <div
                                key={stat.label}
                                data-testid={`hero-stat-${stat.label.replace(/\s+/g, "-").toLowerCase()}`}
                                className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl px-3 py-3 sm:px-5 sm:py-3.5 shadow-lg shadow-slate-200/50"
                            >
                                <div className="font-display font-bold text-xl sm:text-2xl gradient-text">{stat.value}</div>
                                <div className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-snug">{stat.label}</div>
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden mt-10 -mr-4 sm:-mr-8 flex justify-end"
                        data-testid="hero-illustration-mobile"
                    >
                        <img
                            src={IMAGES.hero}
                            alt="Hand-drawn Brain Brush team sketch — the creative people behind the studio"
                            className="h-72 sm:h-96 w-auto max-w-none object-contain mix-blend-multiply"
                            loading="eager"
                        />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
